import { useState } from "react";
import { Box, Group, Stack, Text } from "@mantine/core";
import { useDraggable, useDroppable } from "@dnd-kit/core";
import { useTranslation } from "react-i18next";

import FieldChip from "../FieldChip/FieldChip";
import { useDeleteGroupByMutation } from "../../hooks/mutations";
import { DeleteConfirmModal } from "../../../DeleteConfirmationModal";
import {
  DRAG_GROUP_BY,
  DROP_GROUP_BY_TARGET,
  groupByTargetId,
} from "../../dnd/kinds";
import classes from "./GroupBy.module.scss";

/**
 * The "Group By" section. Drop an entity-field to append it (postGroupBy).
 */
export default function GroupBy({
  ruleId,
  queryId,
  groups,
  canEdit = true,
  isSubquery = false,
}) {
  const { t } = useTranslation();
  const { setNodeRef, isOver } = useDroppable({
    id: groupByTargetId(queryId),
    data: { kind: DROP_GROUP_BY_TARGET, queryId },
  });

  const fullWidth = isSubquery;

  return (
    <Stack gap={4} w={fullWidth ? "100%" : undefined}>
      <Text size="sm" fw={600}>
        {t("dataFields.groupBy")}
      </Text>
      <Box
        ref={setNodeRef}
        className={`${classes.wrapper} ${isOver ? classes.over : ""}`}
      >
        {!groups?.length ? (
          <Text size="sm" c="dimmed" ta="center" py="sm">
            {t("dataFields.dropDataField")}
          </Text>
        ) : (
          <Group gap={6} className={classes.list}>
            {groups.map((group) => (
              <GroupByChip
                key={group.id}
                ruleId={ruleId}
                queryId={queryId}
                groupItem={group}
                canEdit={canEdit}
              />
            ))}
          </Group>
        )}
      </Box>
    </Stack>
  );
}

function GroupByChip({ ruleId, queryId, groupItem, canEdit }) {
  const [deleteOpened, setDeleteOpened] = useState(false);
  const { mutate: deleteGroupBy } = useDeleteGroupByMutation({ ruleId });

  const {
    attributes,
    listeners,
    setNodeRef: setDragRef,
    isDragging,
  } = useDraggable({
    id: `group-by-drag::${groupItem.id}`,
    data: { kind: DRAG_GROUP_BY, groupItem, queryId },
    disabled: !canEdit,
  });

  return (
    <>
      <FieldChip
        ruleId={ruleId}
        entityField={groupItem?.entityField}
        canEdit={canEdit}
        drag={canEdit}
        onDelete={() => setDeleteOpened(true)}
        dragRef={setDragRef}
        dragHandleProps={{ ...attributes, ...listeners }}
        dragStyle={{ opacity: isDragging ? 0.4 : 1 }}
      />
      <DeleteConfirmModal
        opened={deleteOpened}
        onClose={() => setDeleteOpened(false)}
        onDelete={() => {
          deleteGroupBy(groupItem.id);
          setDeleteOpened(false);
        }}
        label="Delete data field?"
      />
    </>
  );
}
